import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { z } from "zod";
import { api } from "../client";
import {
  PropCreate,
  PropRead,
  PropUpdate,
  type PropCreate as PropCreateT,
  type PropUpdate as PropUpdateT,
} from "../schemas";
import { qk } from "../queryKeys";

const PropList = z.array(PropRead);

export function useProps(projectId: number) {
  return useQuery({
    queryKey: qk.props(projectId),
    queryFn: () => api.get(`/api/projects/${projectId}/props`, PropList),
    enabled: projectId > 0,
  });
}

export function useCreateProp(projectId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: PropCreateT) =>
      api.post(`/api/projects/${projectId}/props`, PropCreate.parse(input), PropRead),
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.props(projectId) }),
  });
}

export function useUpdateProp(projectId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, patch }: { id: number; patch: PropUpdateT }) =>
      api.patch(`/api/props/${id}`, PropUpdate.parse(patch), PropRead),
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.props(projectId) }),
  });
}

export function useDeleteProp(projectId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.delete(`/api/props/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.props(projectId) }),
  });
}
